// Venues — API-synced theatres. Reads /venues + joins with /productions +
// /campaigns to surface "what runs here" and "what's wired up". The one
// operator-editable field (`seats`) is wired to PATCH /venues/{id}; everything
// else mirrors EncorePro, so there is deliberately no manual venue create.
//
// Field renames vs. MOCK.VENUES:
//   v.type                  → v.venue_type
//   v.address               → v.address_1 (+ v.address_2, v.city, v.postcode, v.state)
//   v.source                → v.seats_source (binding: 'api' | 'manual')
//   v.last_synced_min_ago   → derived from v.last_synced_at (ISO)
//   v.added_by              → not in canonical; rendered as "Added manually" placeholder
//
// Drops from MOCK (no canonical source — Phase D / reporting roll-ups):
//   - per-production capacity utilization (production.capacity)
//   - week_gross at the venue level
//   - perfs_this_week
// These display as "—" until a reporting hook surfaces them.

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Icons, ChannelPill, StatusDot, fmt, MoneyCell } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import { venuesList } from '@/api/resources';
import { VENUES_SCREEN_EVENT_RANGE, eventsRangeQuery } from '@/lib/event-ranges';
import { campaignKindOf } from '@/lib/campaign-status';
import { useCampaigns, useEvents, useProductions, useVenues } from '@/api/hooks';
import { api } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { queryClient } from '@/cache/queryClient';
import { useCan } from '@/auth/capabilities';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { SourcePill, ProductionStagePill } from './productions';
import { fmtRelative } from './admin-user-drawer';
import { PageActions } from '@/components/page-actions';
import type { Campaign, Event, Production, Venue } from '@/api/types';

/** One KPI cell: label, value, optional sub-line, and whether the sub-line is a rich block. */
type KpiCell = [label: string, value: ReactNode, sub?: ReactNode, rich?: boolean];

interface VenueTypeMeta { color: string }

const VENUE_TYPE_BROADWAY: VenueTypeMeta = { color: 'var(--ink-1)' };

// Any other `venue_type` (free text from EncorePro) renders in the Broadway tone.
const VENUE_TYPE_META: Partial<Record<string, VenueTypeMeta>> = {
  'Broadway':     VENUE_TYPE_BROADWAY,
  'Off-Broadway': { color: 'var(--accent)' },
  'Tour':         { color: 'var(--ink-2)' },
};

function VenueTypePill({ type }: { type: string | null }) {
  const m = (type != null ? VENUE_TYPE_META[type] : undefined) || VENUE_TYPE_BROADWAY;
  return (
    <span style={{
      fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.06em',fontWeight:600,
      padding:'2px 7px',borderRadius:3,
      border:'1px solid color-mix(in oklch, ' + m.color + ' 35%, transparent)',
      color: m.color,
      background:'color-mix(in oklch, ' + m.color + ' 8%, transparent)',
      textTransform:'uppercase',
    }}>{type || 'UNKNOWN'}</span>
  );
}

function NotSetButton({ onClick }: { onClick: (e: MouseEvent<HTMLButtonElement>) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Seats haven't been configured yet — capacity utilization can't be computed for productions in this venue."
      style={{
        background:'transparent',
        border:'1px dashed color-mix(in oklch, var(--bad) 60%, transparent)',
        color:'var(--bad)',
        fontFamily:'var(--font-mono)',fontSize:11,letterSpacing:'.04em',
        padding:'4px 10px',borderRadius:'var(--r-sm, 4px)',
        cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,
      }}
    >not set →</button>
  );
}

function syncedRel(v: Venue | null | undefined) {
  if (!v?.last_synced_at) return '—';
  return fmtRelative(+new Date(v.last_synced_at));
}

function addressOf(v: Pick<Venue, 'address_1' | 'address_2'> | null | undefined) {
  if (!v) return '';
  return [v.address_1, v.address_2].filter(Boolean).join(' ').trim();
}

// Aggregate per-venue metrics from /productions + /campaigns. Phase C: drops
// utilization / week_gross / perfs derivations from MOCK — they sourced from
// production.capacity / production.weekGross / a hardcoded perfs/week number,
// none of which are on the canonical Production binding yet.
function deriveVenueMetrics(
  venue: Venue,
  productions: readonly Production[],
  campaigns: readonly Campaign[],
  eventsThisWeek: readonly Event[] = [],
) {
  const venueShows = productions.filter(s => s.venue_id === venue.id);
  // Match shared::Production::is_running exactly: only `closed` is terminal,
  // and a missing tag intentionally remains in the running slate.
  const running = venueShows.filter(s => s.tag !== 'closed');
  const productionIdsHere = new Set(venueShows.map(s => s.id));
  const venueCampaigns = campaigns.filter(c => productionIdsHere.has(c.production_id));
  const live_campaigns = venueCampaigns.filter(c => campaignKindOf(c) === 'live');
  return {
    productions: venueShows,
    running,
    // Money/ticket metrics are rolled up per venue by the EncorePro ingest (D1 columns);
    // week_gross is trailing-7d. Utilization = sold ÷ available = lifetime tickets
    // ÷ (seats × events); null until a seat count is seeded.
    utilization: (venue.seats && venue.events && venue.tickets != null)
      ? venue.tickets / (venue.seats * venue.events)
      : null,
    week_gross: venue.week_gross_cents != null ? venue.week_gross_cents / 100 : null,
    perfs_this_week: eventsThisWeek.filter(e => e.venue_id === venue.id).length,
    tickets: venue.tickets ?? null,
    avg_price_cents: venue.avg_price_cents ?? null,
    events_count: venue.events ?? null,
    live_campaigns,
    total_campaigns: venueCampaigns.length,
  };
}

function utilColor(_u: number) {
  // Utilization has no central system metric yet. Keep the value diagnostic
  // and neutral rather than inventing a health classification in the browser.
  return 'var(--ink-1)';
}

/** A venue whose fill can be computed: seats and events set, tickets known. */
type UtilizationVenue = Venue & { seats: number; events: number; tickets: number };

function hasUtilization(v: Venue): v is UtilizationVenue {
  return !!(v.seats && v.events && v.tickets != null);
}

interface VenuesKpiStripProps {
  venues: readonly Venue[];
  productions: readonly Production[];
  campaigns: readonly Campaign[];
}

function VenuesKpiStrip({ venues, productions, campaigns }: VenuesKpiStripProps) {
  const configured   = venues.filter(v => v.seats != null);
  const unconfigured = venues.length - configured.length;
  const cfgPct = venues.length ? (configured.length / venues.length) * 100 : 0;
  const cfgColor = unconfigured === 0 ? 'var(--good)' : 'var(--warn)';

  const totalSeats = configured.reduce((a, v) => a + (v.seats || 0), 0);
  const totalWeekGross = venues.reduce((a, v) => a + (v.week_gross_cents || 0), 0);
  // Portfolio fill = Σ tickets ÷ Σ (seats × events) over seat-configured venues.
  const utilVenues = venues.filter(hasUtilization);
  const availSeats = utilVenues.reduce((a, v) => a + v.seats * v.events, 0);
  const soldTickets = utilVenues.reduce((a, v) => a + v.tickets, 0);
  const avgUtil = availSeats > 0 ? soldTickets / availSeats : null;
  let totalRunning = 0;
  venues.forEach(v => {
    const m = deriveVenueMetrics(v, productions, campaigns);
    totalRunning += m.running.length;
  });
  const cfgFootnote = <span className="muted mono" style={{fontSize:9.5,letterSpacing:'.06em'}}>Configured venues only</span>;

  const configuredSub = (
    <>
      <div style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2,display:'flex',justifyContent:'space-between'}}>
        <span><span style={{color:'var(--good)'}}>● </span>{configured.length} set</span>
        <span><span style={{color: unconfigured ? 'var(--bad)' : 'var(--ink-3)'}}>● </span>{unconfigured} unset</span>
      </div>
      <div style={{display:'flex',height:5,background:'var(--bg-sunk)',borderRadius:1,overflow:'hidden',marginTop:4}}>
        <div style={{width: cfgPct + '%',background:'var(--good)'}}/>
        <div style={{width: (100 - cfgPct) + '%',background: unconfigured ? 'var(--bad)' : 'var(--ink-3)'}}/>
      </div>
    </>
  );

  const cells: KpiCell[] = [
    ['Venues tracked',    fmt.nKpi(venues.length)],
    ['Avg utilization',   avgUtil != null ? <span style={{color: utilColor(avgUtil)}}>{Math.round(avgUtil * 100)}%</span> : '—'],
    ['Seats configured',  <>
      <span style={{color: cfgColor}}>{configured.length}</span>
      <span className="muted" style={{fontWeight:400}}> / {venues.length}</span>
    </>, configuredSub, true],
    ['Total seats',       totalSeats ? fmt.nKpi(totalSeats) : '—',  cfgFootnote],
    ['Productions running',     fmt.nKpi(totalRunning)],
    ['Week gross',        totalWeekGross > 0 ? <MoneyCell value={totalWeekGross / 100} dp={2}/> : '—'],
  ];

  return (
    <ResponsiveKpis><div className="kpi-strip" style={{gridTemplateColumns:'repeat(6, 1fr)'}}>
      {cells.map(([k, v, sub, rich]) => (
        <div className="kpi" key={k}>
          <div className="kpi-label">{k}</div>
          <div className="kpi-val">{v}</div>
          {sub && !rich && <div className="kpi-sub" style={{fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',textTransform:'uppercase',color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
          {sub && rich && sub}
        </div>
      ))}
    </div></ResponsiveKpis>
  );
}

interface VenueDetailDrawerProps {
  venue: Venue | undefined;
  productions: readonly Production[];
  campaigns: readonly Campaign[];
  events?: readonly Event[];
  onClose: () => void;
  /** `onDone` runs only once the PATCH lands. */
  onSeatsUpdate?: (id: Venue['id'], seats: number, onDone: () => void) => void;
  seatsSaving: boolean;
  seatsError: Error | null;
  canEdit: boolean;
}

/** The seats input's text for a venue: its seat count, or empty when unset. */
function seatsText(venue: Venue | undefined): string {
  return venue?.seats != null ? String(venue.seats) : '';
}

function VenueDetailDrawer({
  venue, productions, campaigns, events = [], onClose,
  onSeatsUpdate, seatsSaving, seatsError, canEdit,
}: VenueDetailDrawerProps) {
  const [seatsDraft, setSeatsDraft] = useState<string>(seatsText(venue));
  const [editing, setEditing] = useState<boolean>(canEdit && venue?.seats == null);
  const required = useRequiredFeedback();
  useEffect(() => {
    setSeatsDraft(seatsText(venue));
    setEditing(canEdit && venue?.seats == null);
    required.clearAll();
  }, [venue?.id, canEdit, required.clearAll]);
  if (!venue) return null;
  const metrics = deriveVenueMetrics(venue, productions, campaigns, events);

  const saveSeats = () => {
    if (seatsSaving) return;
    const v = parseInt(String(seatsDraft).replace(/[^0-9]/g, ''), 10);
    if (!required.report([
      (!isFinite(v) || v < 1) && {
        key: 'venue-seats',
        message: 'Enter a whole-number capacity of at least 1.',
      },
    ])) return;
    // Editor closes only after the PATCH lands (see onSuccess in the parent);
    // on failure the inline error shows and the input keeps the draft.
    onSeatsUpdate?.(venue.id, v, () => {
      required.clearAll();
      setEditing(false);
    });
  };

  return (
    <>

      <ResponsiveOverlay label="venues details" onClose={onClose} className="drawer">
        <div className="drawer-hd">
          <div style={{flex:1,minWidth:0}}>
            <div className="muted mono" style={{fontSize:10,letterSpacing:'.12em',textTransform:'uppercase'}}>Venue · <span style={{color:'var(--ink-1)'}}>{venue.id}</span></div>
            <h2 style={{fontFamily:'var(--font-display)',fontSize:20,fontWeight:800,letterSpacing:'.01em',margin:'4px 0 6px',textTransform:'uppercase'}}>{venue.name}</h2>
            <div className="flex items-center gap-2" style={{flexWrap:'wrap'}}>
              <VenueTypePill type={venue.venue_type}/>
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>{addressOf(venue)}{venue.neighborhood ? ` · ${venue.neighborhood}` : ''}</span>
              <span title="API-synced · cannot be deleted manually" style={{
                fontFamily:'var(--font-mono)',fontSize:9.5,letterSpacing:'.08em',fontWeight:700,
                padding:'2px 6px',borderRadius:2,
                background:'color-mix(in oklch, var(--accent) 12%, transparent)',
                color:'var(--accent)',
                border:'1px solid color-mix(in oklch, var(--accent) 35%, transparent)',
              }}>API-SYNCED</span>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} style={{fontSize:18,padding:'4px 10px'}}>×</button>
        </div>

        <div className="drawer-body">
          <div className="field-lbl mb-2">At a glance</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div className="pair-phone" style={{display:'grid',gridTemplateColumns:'repeat(3, 1fr)',gap:14}}>
              {([
                ['Seats',           venue.seats != null ? venue.seats.toLocaleString() : <span style={{color:'var(--bad)'}}>not set</span>],
                ['Productions running',   String(metrics.running.length)],
                ['Perfs this week', metrics.perfs_this_week > 0 ? String(metrics.perfs_this_week) : '—'],
                ['Week gross',      venue.week_gross_cents != null ? <MoneyCell value={venue.week_gross_cents / 100}/> : '—'],
                ['Tickets',         venue.tickets != null ? venue.tickets.toLocaleString() : '—'],
                ['Avg ticket',      venue.avg_price_cents != null ? <MoneyCell value={venue.avg_price_cents / 100}/> : '—'],
              ] satisfies Array<[label: string, value: ReactNode]>).map(([k,v]) => (
                <div key={k}>
                  <div className="muted mono" style={{fontSize:9.5,letterSpacing:'.08em',textTransform:'uppercase'}}>{k}</div>
                  <div className="mono" style={{fontSize:18,fontWeight:700,marginTop:2}}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid var(--line-2)',display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:11.5}}>
              <span className="muted mono" style={{fontSize:10.5,letterSpacing:'.06em'}}>Avg utilization</span>
              <span>{metrics.utilization != null
                ? <span className="mono" style={{fontWeight:600,color:utilColor(metrics.utilization)}}>{Math.round(metrics.utilization * 100)}%</span>
                : <span className="muted" style={{fontStyle:'italic',fontSize:11}}>not tracked</span>}</span>
            </div>
          </div>

          <div className="field-lbl mb-2">Seats</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            {!editing ? (
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div>
                  <div className="mono" style={{fontSize:22,fontWeight:700}}>
                    {venue.seats != null
                      ? <>{venue.seats.toLocaleString()} <span className="muted" style={{fontWeight:400,fontSize:13}}>seats</span></>
                      : <span style={{color:'var(--bad)'}}>not set</span>}
                  </div>
                  <div className="field-hint" style={{marginTop:6}}>
                    Operator-editable. Used to compute capacity utilization for productions in this venue.
                  </div>
                </div>
                {canEdit && (
                  <button className="btn btn-sm" onClick={() => {
                    required.clearAll();
                    setEditing(true);
                  }}>
                    {venue.seats != null ? 'Edit' : 'Set seats'}
                  </button>
                )}
              </div>
            ) : (
              <div>
                <RequiredField
                  fieldKey="venue-seats"
                  message={required.messages['venue-seats']}
                  attempt={required.attempt}
                >
                  <label className="field-lbl">Available seats</label>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <input
                      className="input mono"
                      style={{fontSize:14,maxWidth:200}}
                      type="text"
                      inputMode="numeric"
                      autoFocus
                      data-validation-focus
                      placeholder="e.g. 1240"
                      value={seatsDraft}
                      onChange={e => {
                        const next = e.target.value.replace(/[^0-9]/g, '');
                        setSeatsDraft(next);
                        if (Number.parseInt(next, 10) >= 1) required.clear('venue-seats');
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveSeats();
                        if (e.key === 'Escape') {
                          setSeatsDraft(seatsText(venue));
                          required.clearAll();
                          setEditing(false);
                        }
                      }}
                    />
                    <button className="btn btn-sm btn-primary" disabled={seatsSaving} onClick={saveSeats}>
                      {Icons.check} {seatsSaving ? 'Saving…' : 'Save'}
                    </button>
                    {venue.seats != null && (
                      <button className="btn btn-sm btn-ghost" onClick={() => {
                        setSeatsDraft(seatsText(venue));
                        required.clearAll();
                        setEditing(false);
                      }}>Cancel</button>
                    )}
                  </div>
                </RequiredField>
                {seatsError && <div style={{marginTop:8}}><MutationErrorLine error={seatsError}/></div>}
                <div className="field-hint" style={{marginTop:8}}>
                  Whole-number house capacity. Saved to the registry (audited); utilization recomputes across all productions in this venue.
                </div>
              </div>
            )}
          </div>

          <div className="field-lbl mb-2 flex between items-center">
            <span>Productions running here</span>
            <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>{metrics.running.length} running · {metrics.productions.length} total</span>
          </div>
          <div className="panel" style={{padding:0,marginBottom:14,overflow:'hidden'}}>
            {metrics.productions.length === 0 ? (
              <div className="muted" style={{padding:18,textAlign:'center',fontSize:11.5}}>No productions assigned to this venue.</div>
            ) : (
              <table className="tbl" style={{margin:0}}>
                <thead>
                  <tr>
                    <th>Production</th>
                    <th>Tag</th>
                    <th className="num">Avg price</th>
                    <th className="num">Week gross</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.productions.map(s => (
                    <tr key={s.id} style={{cursor:'default'}}>
                      <td>{s.name}</td>
                      <td>{s.tag ? <ProductionStagePill stage={(s.tag || '').toUpperCase()}/> : <span className="muted">—</span>}</td>
                      <td className="num">{s.avg_price_cents != null ? <MoneyCell value={s.avg_price_cents / 100}/> : <span className="muted">—</span>}</td>
                      <td className="num">{s.week_gross_cents != null ? <MoneyCell value={s.week_gross_cents / 100}/> : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="field-lbl mb-2 flex between items-center">
            <span>Campaigns at this venue</span>
            <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>{metrics.live_campaigns.length} live · {metrics.total_campaigns} total</span>
          </div>
          <div className="panel" style={{padding:0,marginBottom:14,overflow:'hidden'}}>
            {metrics.total_campaigns === 0 ? (
              <div className="muted" style={{padding:18,textAlign:'center',fontSize:11.5}}>No campaigns wired up for productions at this venue.</div>
            ) : (
              <table className="tbl" style={{margin:0}}>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th className="num">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.live_campaigns.concat(
                    campaigns.filter(c =>
                      metrics.productions.some(s => s.id === c.production_id) && campaignKindOf(c) !== 'live'
                    )
                  ).slice(0, 10).map(c => (
                    <tr key={c.id} style={{cursor:'default'}}>
                      <td style={{whiteSpace:'normal',maxWidth:240}}>{c.name}</td>
                      <td><ChannelPill channel={c.channel}/></td>
                      <td><StatusDot s={campaignKindOf(c)}/></td>
                      <td className="num">{<MoneyCell value={(c.spent_cents || 0) / 100}/>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="field-lbl mb-2">Sync metadata</div>
          <div className="panel" style={{padding:12,marginBottom:14}}>
            <div style={{display:'grid',gridTemplateColumns:'140px 1fr',rowGap:6,fontSize:12}}>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Last sync</div>
              <div>{syncedRel(venue)}</div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Source</div>
              <div className="mono">EncorePro Venue API</div>
              <div className="muted mono" style={{fontSize:10,letterSpacing:'.06em',textTransform:'uppercase'}}>Editable fields</div>
              <div className="mono" style={{fontSize:11}}>seats</div>
            </div>
          </div>
        </div>

        <div className="drawer-foot">
          <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em'}}>API-synced · only <code className="mono">seats</code> is operator-editable</span>
          <button className="btn btn-sm" style={{marginLeft:'auto'}} onClick={onClose}>Close</button>
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// Manual venue creation was removed deliberately: the slate is
// EncorePro-authoritative (nightly + on-demand backfill upserts) and there is no
// POST /venues route. If manual creates return, restore the modal from git
// history alongside the server route.

// The line under a venue's name in the list (and the phone card's title, which
// is the same cell): "Theatre District · Theatre", or whichever half is known.
// It used to print the venue's id, which the column cut off mid-UUID; the id
// stays in the drawer header. Nothing known, no line, unless the venue has no
// name either: then "Unnamed venue" over its city, else the start of its id, as
// the venue filters label it (`venueOptionLabel`), so the row still says which
// one it is. EncorePro spells the type as it likes ("Broadway", "theatre"), so
// the line starts it upper-case.
function venueSubline(venue: Pick<Venue, 'id' | 'name' | 'city' | 'neighborhood' | 'venue_type'>): string {
  const type = venue.venue_type ? venue.venue_type.charAt(0).toUpperCase() + venue.venue_type.slice(1) : null;
  const known = [venue.neighborhood, type].filter(Boolean).join(' · ');
  return known || (venue.name ? '' : venue.city || venue.id.slice(0, 8));
}

// The Location column: the street address, else the city, so a venue EncorePro
// sent without an address still says where it is. The neighbourhood is on the
// line under the name, and the phone card shows both cells, so it is not
// repeated here.
function locationOf(venue: Venue): string {
  return addressOf(venue) || [venue.city, venue.state_code].filter(Boolean).join(', ');
}

const ROW_H = 56;
const { Table } = createDataTable([
  { key: 'venue',   header: 'Venue',               width: 240, mobile: 'title' },
  { key: 'source',  header: 'Source',              width: 100, priority: 3 },
  { key: 'type',    header: 'Type',                width: 110, priority: 2 },
  { key: 'loc',     header: 'Location',            width: 220, priority: 2, mobile: 'subtitle' },
  { key: 'seats',   header: 'Seats',               width:  90, align: 'num', mobile: 'stat' },
  { key: 'running', header: 'Productions running', width: 280, priority: 2, mobile: 'stat' },
  { key: 'perfs',   header: 'Perfs · this wk',     width: 110, align: 'num', priority: 3 },
  { key: 'util',    header: 'Utilization',         width: 110, align: 'num', priority: 3 },
  { key: 'gross',   header: 'Week gross',          width: 130, align: 'num', mobile: 'stat' },
  { key: 'camps',   header: 'Campaigns',           width: 110, align: 'num', priority: 2, mobile: 'stat' },
  { key: 'sync',    header: 'Last sync',           width: 120, priority: 3 },
]);

type ConfigFilter = 'all' | 'configured' | 'needs_seats';
type SourceFilter = Venue['seats_source'] | 'all';

function VenuesView({ setView: _setView, focus }: { setView?: (viewId: string) => void; focus?: string }) {
  const venuesQuery = useVenues();
  const venues = venuesQuery.data ?? [];
  const { data: productions = [] } = useProductions();
  const { data: campaigns = [] } = useCampaigns();
  // Events this week → per-venue "Perfs · this wk" (scheduled performances).
  // Shared with this route's loader, and previously a hand-rolled second copy
  // of the same NY-week arithmetic. See lib/event-ranges.ts.
  const thisWeekRange = useMemo(() => eventsRangeQuery(VENUES_SCREEN_EVENT_RANGE), []);
  // Kept whole: /events refuses a window past 1,000 performances, and a
  // swallowed refusal would render every venue's count as 0.
  const eventsQuery = useEvents(thisWeekRange);
  const events = eventsQuery.data ?? [];

  const [q, setQ]                 = useState('');
  const [cfgFilter, setCfgFilter] = useState<ConfigFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  // Only the id: the drawer re-resolves the venue from the live list, so a
  // deep link (?focus=<id>) and a row click select the same way.
  const [selected, setSelected]   = useState<{ id: Venue['id'] } | null>(null);
  // Deep-link ?focus=<id> opens that venue's drawer (re-resolves by id).
  useEffect(() => { if (focus) setSelected({ id: focus }); }, [focus]);
  const canEdit = useCan('backend_edit', 'own');

  // The one operator write on this screen: seats override, audited server-side.
  const seatsM = useRegisteredMutation({
    domains: ['catalog'],
    mutationFn: ({ id, seats }: { id: Venue['id']; seats: number }) =>
      api.patch(`/venues/${encodeURIComponent(id)}`, { body: { seats } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: venuesList.all }),
  });
  const onSeatsUpdate = (id: Venue['id'], seats: number, onDone: () => void) =>
    seatsM.mutate({ id, seats }, { onSuccess: () => onDone?.() });

  const sorted = useMemo(() => [...venues].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [venues]);

  const rows = sorted.filter(v => {
    if (cfgFilter === 'configured' && v.seats == null) return false;
    if (cfgFilter === 'needs_seats' && v.seats != null) return false;
    if (typeFilter !== 'all' && v.venue_type !== typeFilter) return false;
    if (sourceFilter !== 'all' && v.seats_source !== sourceFilter) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      const hay = [v.name, v.neighborhood, addressOf(v)].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });


  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Venues</h1>
        </div>
        <PageActions>
          <span className="muted mono" data-phone="hidden" title="The venue slate mirrors EncorePro (nightly + on-demand backfill). Only seats is operator-editable." style={{fontSize:10, letterSpacing:'.08em', textTransform:'uppercase', alignSelf:'center', marginRight:4}}>
            ⟳ Synced from EncorePro
          </span>
          <button className="btn btn-icon-phone" data-phone="inline" aria-label="Export CSV" onClick={() => downloadCsv(csvFilename('venues'), rows.map(v => {
            const m = deriveVenueMetrics(v, productions, campaigns, events);
            return {
              'Venue': v.name || '',
              'ID': v.id,
              'Source': v.seats_source || 'api',
              'Type': v.venue_type || '',
              'Address': addressOf(v),
              'Neighborhood': v.neighborhood || '',
              'Seats': v.seats ?? '',
              'Productions running': m.running.length,
              'Perfs this week': m.perfs_this_week,
              'Utilization %': m.utilization != null ? Math.round(m.utilization * 100) : '',
              'Week gross $': v.week_gross_cents != null ? (v.week_gross_cents / 100).toFixed(2) : '',
              'Live campaigns': m.live_campaigns.length,
              'Total campaigns': m.total_campaigns,
              'Last synced': v.last_synced_at || '',
            };
          }))}><span>{Icons.dl}</span><span className="phone-long">Export CSV</span></button>
        </PageActions>
      </div>

      <VenuesKpiStrip venues={rows} productions={productions} campaigns={campaigns}/>

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0'}}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter venues by name, address, or neighborhood"/>
        </div>
        <Dropdown value={cfgFilter} options={[
          { id:'all',         label:'All venues' },
          { id:'configured',  label:'Seats configured' },
          { id:'needs_seats', label:'Needs seats' },
        ]} onChange={setCfgFilter} label="Config"/>
        <Dropdown value={typeFilter} options={[
          { id:'all', label:'All types' },
          { id:'Broadway', label:'Broadway' },
          { id:'Off-Broadway', label:'Off-Broadway' },
          { id:'Tour', label:'Tour' },
        ]} onChange={setTypeFilter} label="Type"/>
        <Dropdown value={sourceFilter} options={[
          { id:'all',    label:'All sources' },
          { id:'api',    label:'API-synced' },
          { id:'manual', label:'Manually added' },
        ]} onChange={setSourceFilter} label="Source"/>
      </ResponsiveFilters>

      {eventsQuery.isError && (
        <QueryErrorNote error={eventsQuery.error} what="this week's performances" onRetry={() => { void eventsQuery.refetch(); }} />
      )}

      <Table
        rows={rows}
        rowH={ROW_H}
        unfilteredTotal={venues.length}
        deps={[{ what: 'venues', query: venuesQuery }]}
        empty="No venues yet. They sync in from EncorePro automatically."
        noMatch="No venues match these filters."
        onRowClick={setSelected}
        renderRow={(v) => {
          const m = deriveVenueMetrics(v, productions, campaigns, events);
          const liveProductionTitles = m.running.map(s => s.name).slice(0, 2);
          const more = Math.max(0, m.running.length - 2);
          return (
            <>
              <td className="name" style={{lineHeight:1.25}}>
                <div style={{display:'flex',flexDirection:'column',minWidth:0,overflow:'hidden'}}>
                  <span style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v.name || 'Unnamed venue'}</span>
                  {venueSubline(v) && (
                    <span className="muted mono" style={{fontSize:10,letterSpacing:'.06em',fontWeight:400,marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{venueSubline(v)}</span>
                  )}
                </div>
              </td>
              <td><SourcePill source={v.seats_source || 'api'}/></td>
              <td><VenueTypePill type={v.venue_type}/></td>
              <td className="mono" style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.2}}>
                <span style={{display:'block',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{locationOf(v) || '—'}</span>
              </td>
              <td className="num">
                {v.seats != null
                  ? <span className="mono" style={{fontWeight:600}}>{v.seats.toLocaleString()}</span>
                  : <NotSetButton onClick={(e) => { e.stopPropagation(); setSelected(v); }}/>}
              </td>
              <td style={{lineHeight:1.3,minWidth:0,overflow:'hidden'}}>
                {m.running.length === 0
                  ? <span className="muted" style={{fontStyle:'italic',fontSize:11}}>—</span>
                  : (
                    <span className="mono" style={{fontSize:11,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',display:'block'}}>
                      <b style={{fontFamily:'var(--font-ui)',fontWeight:600}}>{m.running.length}</b>
                      <span className="muted" style={{marginLeft:6,fontFamily:'var(--font-ui)'}}>{liveProductionTitles.join(' · ')}{more > 0 ? ` · +${more} more` : ''}</span>
                    </span>
                  )}
              </td>
              <td className="num mono">{m.perfs_this_week > 0 ? <span style={{fontWeight:600}}>{m.perfs_this_week}</span> : <span className="muted">—</span>}</td>
              <td className="num mono">{m.utilization != null ? <span style={{fontWeight:600,color:utilColor(m.utilization)}}>{Math.round(m.utilization * 100)}%</span> : <span className="muted">—</span>}</td>
              <td className="num">{v.week_gross_cents != null ? <MoneyCell value={v.week_gross_cents / 100}/> : <span className="muted">—</span>}</td>
              <td className="num">
                <span className="mono" style={{fontWeight:600}}>{m.live_campaigns.length}</span>
                {m.total_campaigns > m.live_campaigns.length && (
                  <span className="muted mono" style={{fontSize:10,marginLeft:4}}>/ {m.total_campaigns}</span>
                )}
              </td>
              <td className="mono" style={{fontSize:11,color:'var(--ink-2)'}}>{syncedRel(v)}</td>
            </>
          );
        }}
      />

      {selected && (
        <VenueDetailDrawer
          venue={venues.find(v => v.id === selected.id)}
          productions={productions}
          campaigns={campaigns}
          events={events}
          onClose={() => { setSelected(null); seatsM.reset(); }}
          onSeatsUpdate={onSeatsUpdate}
          seatsSaving={seatsM.isPending}
          seatsError={seatsM.error}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}

export { VenuesView, VenueDetailDrawer, VenueTypePill };
